<template>
	<div class="p-p-2">
		<BlockUI :blocked="loading">
			<DocumentClassForm :documentClass="documentClass" :showErrorMessage="showErrorMessage" v-model:llmActive="llmActive" @update="onUpdate" />
		</BlockUI>
		<div class="p-fluid p-formgrid p-grid p-jc-end p-p-2">
			<div class="p-field p-col-3">
				<Button :disabled="disableButton" :label="$t('Squeeze.DocumentClasses.RebuildSearchIndex')" type="button" class="p-button" @click="rebuildSearchIndex" />
			</div>
			<div class="p-offset-3"></div>
			<div class="p-field p-col-3">
				<Button v-if="store.state.featureSet.uiConfigExport" :disabled="disableButton" label="Export" type="button" icon="mdi mdi-database-export" class="p-button" @click="exportDocumentClass" />
			</div>
			<div class="p-field p-col-3">
				<Button :disabled="loading" :label="saveButtonText" type="button" icon="mdi mdi-content-save-outline" class="p-button" @click="saveDocumentClass" />
			</div>
		</div>
	</div>

	<EntryDialog
		:show="showExportDialog"
		:closable="!showProcessBar"
		@onClose="showExportDialog = false"
		headerText="Export"
		:showSaveButton="false"
		:showAbortButton="!showProcessBar"
		:abortKeydownEvent="showProcessBar"
		:abortButtonText="$t('Squeeze.General.Abort')"
	>
		<template #content>
			<div v-if="showProcessBar">
				<ProgressBar mode="indeterminate" style="height: 6px" />
			</div>
			<div v-else>
				<p>{{ $t('Squeeze.General.SuccessfulExport', { entryName: $t('Squeeze.DocumentClasses.DocumentClass')}) }}</p>
				<div class="p-fluid p-formgrid p-grid p-jc-end">
					<div class="p-field p-col-12">
						<Button :label="$t('Squeeze.General.CopyToClipboard')" icon="mdi mdi-content-copy" @click="copyToClipboard($t, $toast, documentClassData), documentClassData = '', showExportDialog = false" />
					</div>
					<div class="p-field p-col-12">
						<Button :label="$t('Squeeze.General.Download')" icon="mdi mdi-file-download-outline" @click="downloadData(documentClass.name, documentClassData), documentClassData = '', showExportDialog = false" />
					</div>
				</div>
			</div>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import useSqueezeStore from "@/apps/squeeze/store";
import DocumentClassForm from "@/apps/administration/components/documentclasses/DocumentClassForm.vue";
import BlockUI from "primevue/blockui";
import EntryDialog from "@/components/EntryDialog.vue";
import {ClientManager} from "@/singletons/ClientManager";
import {DocumentClass} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import router from "@/router";
import {Clients, ConsoleLogger, Exporter, SqueezeConfig} from "@dex/squeeze-configframework";
import {copyToClipboard, downloadData} from "@/util/Export";
import ProgressBar from "primevue/progressbar";
import {LLM_EXTRACTION_SETTING, useDocumentClassSettings} from "@/composables/useDocumentClassSettings";

export default defineComponent({
	name: 'DocumentClassSubView',
	methods: {copyToClipboard, downloadData},
	components: {
		ProgressBar,
		DocumentClassForm,
		BlockUI,
		EntryDialog,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current Vuex-Store */
		const store = useSqueezeStore();

		/**
		 * Settings of the document class, shared with DocumentClassView, which derives the
		 * visible tabs from them.
		 */
		const {setDocumentClassSettings, isDocumentClassSettingActive} = useDocumentClassSettings();

		/** Service for Document-Classes */
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		/** Currently active Document-Class */
		const documentClass = reactive<DocumentClass>({
			name: '',
			description: '',
		});

		/** Current value of the LLMExtraction setting */
		const llmActive = ref<boolean>(false);

		/** Is the page currently loading its data? */
		const loading = ref<boolean>(false);

		/** Show button to rebuild index only if not already loading or document class does not yet exist */
		const disableButton = computed(() => {
			return loading.value || !props.documentClassId;
		});

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Triggered the valid of form */
		const isInvalid = ref<boolean>(true);

		/** Current save-button text */
		const saveButtonText = ref<string>('');

		/** Exported document class data as string  */
		const documentClassData = ref<string>('');

		/** Is Export Dialog visible? */
		const showExportDialog = ref<boolean>(false);

		/** Is the Loading Process Bar visible? */
		const showProcessBar = ref<boolean>(false);

		const getTranslateOfSaveButton = () => {
			if (!props.documentClassId) {
				saveButtonText.value = t('Squeeze.General.CreateEntry', { entryName: t('Squeeze.DocumentClasses.DocumentClass') });
			} else {
				saveButtonText.value = t('Squeeze.General.Save');
			}
		}

		/** Loads the current value of the LLMExtraction document class setting (only when the FeatureFlag is active) */
		const getLlmSetting = () => {
			if (!store.state.featureSet.llmExtraction || !props.documentClassId) {
				return;
			}

			documentClassService.getDocumentClassSettings(props.documentClassId)
				.then(settings => {
					setDocumentClassSettings(settings);
					llmActive.value = isDocumentClassSettingActive(LLM_EXTRACTION_SETTING);
				})
				.catch(() => {
					// if something is missing, set to false (default false)
					llmActive.value = false;
				});
		}

		/**
		 * Persists the LLMExtraction setting for the given document class (only when the FeatureFlag is active).
		 * @param {number} documentClassId ID of the document class the setting is scoped to
		 */
		const saveLlmSetting = (documentClassId: number): Promise<void> => {
			if (!store.state.featureSet.llmExtraction || !documentClassId) {
				return Promise.resolve();
			}

			return documentClassService.putDocumentClassSettingByName(documentClassId, LLM_EXTRACTION_SETTING, String(llmActive.value))
				// Re-read the settings so the tabs follow the saved value without a page reload.
				.then(() => documentClassService.getDocumentClassSettings(documentClassId))
				.then(settings => {
					setDocumentClassSettings(settings);
				});
		}

		/** Gets the Data of a Document-Class */
		const getDocumentClass = () => {
			if (props.documentClassId) {
				loading.value = true;
				documentClassService.getDocumentClassById(props.documentClassId)
					.then(data => {
						Object.assign(documentClass, data);
						loading.value = false;
					})
					.catch((err: { statusText: string }) => {
						loading.value = false;
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.DocumentClasses.DocumentClass') + ": " + err.statusText);
					});

				getLlmSetting();
			}
		}

		/**
		 * Triggered on update of attribute-form
		 * @param {DocumentClass} data
		 * @param {boolean} valid
		 */
		const onUpdate = (data: DocumentClass, valid: boolean) => {
			isInvalid.value = valid;
			Object.assign(documentClass, data);
		}

		/** Rebuild the search index */
		const rebuildSearchIndex = async () => {
			if (!props.documentClassId) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError'));
				return;
			}

			loading.value = true;

			try {
				await documentClassService.rebuildDocumentClassSearchIndex(props.documentClassId);
			} catch (error) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), error);
			} finally {
				loading.value = false;
			}
		}

		/** Saves a document-class */
		const saveDocumentClass = () => {
			if (isInvalid.value) {
				showErrorMessage.value = true;
				return;
			}

			let promise;

			showErrorMessage.value = false;
			loading.value = true;

			if (!documentClass.id) {
				// Create New Entry
				promise = documentClassService.postDocumentClass(documentClass)
					.then(data => saveLlmSetting(data.id as number)
						.then(() => {
							ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.CreateSuccess'));
							if (data.id) {
								router.push({ name: 'DocumentClassFieldGroupsSubView', params: { documentClassId: data.id}})
							}
						}))
			} else {
				// Update Entry
				promise = documentClassService.putDocumentClass(props.documentClassId, documentClass)
					.then(() => saveLlmSetting(props.documentClassId))
					.then(() => ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess')));
			}

			promise
				.catch((err: { statusText: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError') + ": " + err.statusText);
				})
				.finally(() => {
					loading.value = false;
				});
		}

		/** Run export of document class */
		const runExportOfDocumentClass = async (exporter: Exporter) => {
			showExportDialog.value = true;
			showProcessBar.value = true;
			loading.value = true;
			documentClassData.value = '';

			try {
				const docClassData: SqueezeConfig = await exporter.run()
					.finally(() => {
						showProcessBar.value = false;
					});
				documentClassData.value = JSON.stringify(docClassData, null, 2);
			} catch (error) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), error);
			} finally {
				loading.value = false;
			}
		}

		/** Export of document class */
		const exportDocumentClass = async () => {
			const clients: Clients = {
				documentClass: documentClassService,
				masterData: ClientManager.getInstance().squeeze.masterData,
				locator: ClientManager.getInstance().squeeze.locator,
			}
			const exporter = new Exporter(clients, new ConsoleLogger());

			if (documentClass.name) {
				exporter.addDocumentClass(documentClass.name);

				await runExportOfDocumentClass(exporter);
			}
		}

		onMounted(() =>{
			getDocumentClass();
			getTranslateOfSaveButton();
		})

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(() => props.documentClassId, () => {
			getDocumentClass();
		})

		return {
			t,
			toast,
			store,
			disableButton,
			documentClass,
			llmActive,
			loading,
			showErrorMessage,
			isInvalid,
			saveButtonText,
			documentClassData,
			showExportDialog,
			showProcessBar,
			getTranslateOfSaveButton,
			getDocumentClass,
			onUpdate,
			rebuildSearchIndex,
			saveDocumentClass,
			runExportOfDocumentClass,
			exportDocumentClass,
		};
	},
});

</script>

<style scoped>
</style>
